import {
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
} from '@mui/x-data-grid-pro';
import FilterTextField from '../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {t} from 'i18next';
import ArrButton from '../../common/arr-button/ArrButton';
import {Typography} from '@mui/material';
import CellWrapper from '../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {ReactComponent as ThreeDotMenu} from '../../../assets/images/three-dot-menu.svg';
import FilterDateField from '../../common/arr-data-grid-pro/filter-components/date-time-field/FilterDateField';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import {IndexManagementData} from '../../../api/types/ingestionApi.type';
import './IndexManagementData.scss';

const HEADER_MIN_WIDTH = 171;
const SECURITY_GROUP_COLUMN_WIDTH = 180;

const getStatusClass = (value: string) => {
  switch (value) {
    case 'COMPLETE':
      return 'status-completed';
    case 'FAILED':
      return 'status-failed';
    case 'PROCESSING':
      return 'status-inprogress';
    case 'QUEUED':
      return 'status-queues';
    default:
      return '';
  }
};

const EMPTY_SECURITY_GROUPS_PLACEHOLDER = '---';

const formatSecurityGroups = (value: unknown): string => {
  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
      .join(', ');
    return formatted || EMPTY_SECURITY_GROUPS_PLACEHOLDER;
  }
  if (typeof value === 'string') {
    const formatted = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .join(', ');
    return formatted || EMPTY_SECURITY_GROUPS_PLACEHOLDER;
  }
  return value == null ? EMPTY_SECURITY_GROUPS_PLACEHOLDER : String(value);
};

const cellWrapper = (cellvalue: any, params: GridRenderCellParams) => {
  const cell = (
    <CellWrapper
      toolTipContent={cellvalue}
      dataTestId={`cell-wrapper-tooltip-content-${params.id}`}
      cellParams={params}
    >
      <span
        tabIndex={0}
        className={
          params.field == 'status'
            ? `cell-label typo-text body3_size ${getStatusClass(
                params.value as string
              )}`
            : 'cell-label'
        }
        data-testid={`cell-indexmanagement-${params.field}-${params.id}`}
        style={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {cellvalue}
      </span>
    </CellWrapper>
  );
  return cell;
};

export const columns = (
  handleMoreIconClick: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    clientData: IndexManagementData
  ) => void,
  handleViewIndex: (row: IndexManagementData) => void
) => [
  {
    field: '',
    headerName: '',
    width: 32,
    filterable: false,
    sortable: false,
    cellClassName: 'grid-action-icon',
    renderCell: (params: GridRenderCellParams) => {
      return (
        <ArrIconButton
          icon={<ThreeDotMenu />}
          onClick={(e) => handleMoreIconClick(e, params.row)}
          ariaLabel={`Menu options`}
          shape="none"
          size="medium"
          data-testid={`draggable-menu-${params.id}`}
          testId="index-management-data-icon-button"
        />
      );
    },
  },
  {
    field: 'indexName',
    headerName: t('ingestions.indexManagement.columns.indexName'),
    minWidth: HEADER_MIN_WIDTH,
    renderHeader: () => (
      <Typography
        data-testid="index-name-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.indexName')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) => {
      const cellContent = (
        <ArrButton
          variant="text"
          size="xs"
          className="id-cell-preview"
          dataTestId={`index-management-indexName-link-${params.id}`}
          onClick={() => handleViewIndex(params.row)}
        >
          <Typography className="cell-label typo-text body3_size">
            {params.value}
          </Typography>
        </ArrButton>
      );
      return (
        <CellWrapper
          toolTipContent={params.value}
          dataTestId={`cell-wrapper-tooltip-content-${params.id}`}
          cellParams={params}
        >
          {cellContent}
        </CellWrapper>
      );
    },
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          dataTestId={`indexName`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.filter')}
          className="custom-filter"
        />
      );
    },
  },
  {
    field: 'securityGroups',
    headerName: t('ingestions.indexManagement.columns.securityGroup'),
    width: SECURITY_GROUP_COLUMN_WIDTH,
    minWidth: SECURITY_GROUP_COLUMN_WIDTH,
    renderHeader: () => (
      <Typography
        data-testid="security-group-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.securityGroup')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(formatSecurityGroups(params.value), params),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          dataTestId={`securityGroup`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.filter')}
          className="custom-filter"
        />
      );
    },
  },
  {
    field: 'status',
    headerName: t('ingestions.indexManagement.columns.status'),
    minWidth: HEADER_MIN_WIDTH,
    cellClassName: 'status-cell',
    renderHeader: () => (
      <Typography
        data-testid="status-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.status')}
      </Typography>
    ),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          dataTestId={`status`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.filter')}
          className="custom-filter"
        />
      );
    },
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(params.value, params),
  },
  {
    field: 'docCount',
    headerName: t('ingestions.indexManagement.columns.docCount'),
    minWidth: HEADER_MIN_WIDTH,
    filterable: false,
    renderHeader: () => (
      <Typography
        data-testid="doc-count-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.docCount')}
      </Typography>
    ),

    renderCell: (params: GridRenderCellParams) => {
      const cellContent = (
        <ArrButton
          disabled={true}
          variant="text"
          size="xs"
          className="id-cell-preview"
          dataTestId={`index-management-data-button-${params.id}`}
        >
          <Typography
            className="doc-id-cell typo-text body3_size"
            data-testid={`index-management-data-docCount-${params.id}`}
          >
            {params.value}
          </Typography>
        </ArrButton>
      );
      return (
        <CellWrapper
          dataTestId={`doclist-column-${params.value}`}
          cellParams={params}
        >
          {cellContent}
        </CellWrapper>
      );
    },
  },
  {
    field: 'fieldCount',
    headerName: t('ingestions.indexManagement.columns.fieldCount'),
    renderHeader: () => (
      <Typography
        data-testid="field-count-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.fieldCount')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(params.value, params),
    minWidth: HEADER_MIN_WIDTH,
    filterable: false,
  },
  {
    field: 'createDate',
    headerName: t('ingestions.indexManagement.columns.createDate'),
    minWidth: HEADER_MIN_WIDTH,
    filterable: false,
    renderHeader: () => (
      <Typography
        data-testid="create-date-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.createDate')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(params.value, params),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterDateField
          dataTestId={`filter-datePicker-${params.colIndex}`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.datePlaceholder')}
        />
      );
    },
  },
  {
    field: 'lastIndexDate',
    headerName: t('ingestions.indexManagement.columns.lastIndexedDate'),
    minWidth: HEADER_MIN_WIDTH,
    filterable: false,
    renderHeader: () => (
      <Typography
        data-testid="last-indexed-date-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.lastIndexedDate')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(params.value, params),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterDateField
          dataTestId={`filter-datePicketer-lastIndexDate`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.datePlaceholder')}
        />
      );
    },
  },
  {
    field: 'userCreated',
    headerName: t('ingestions.indexManagement.columns.userCreated'),
    renderHeader: () => (
      <Typography
        data-testid="user-created-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.userCreated')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(params.value, params),
    minWidth: HEADER_MIN_WIDTH,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          dataTestId={`userCreated`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.filter')}
          className="custom-filter"
        />
      );
    },
  },
  {
    field: 'userLastIndex',
    headerName: t('ingestions.indexManagement.columns.userLastIndex'),
    minWidth: HEADER_MIN_WIDTH,
    renderHeader: () => (
      <Typography
        data-testid="user-last-index-header"
        tabIndex={0}
        className="typo-text label2_size"
      >
        {t('ingestions.indexManagement.columns.userLastIndex')}
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams) =>
      cellWrapper(params.value, params),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          dataTestId={`userLastIndex`}
          gridRenderHeaderFilterProps={params}
          placeholder={t('common.filter')}
          className="custom-filter"
        />
      );
    },
  },
];
